/** @file The timelines the curve graph shows: the selected keys', bones' and slots' numeric channels. */
import type { Doc, Id } from '@pose2d/format/types';
import type { SelectionSlice } from '../../stores/types';
import { buildCurveTrack } from './buildCurveTrack';
import { CURVE_CHANNELS } from './curveChannels.data';
import type { CurveChannel, CurveRef } from './curveTypes';
import { toCurveRef } from './toCurveRef';

const ORDER = Object.keys(CURVE_CHANNELS) as CurveChannel[];
const BONE_CHANNELS = ORDER.filter((c) => CURVE_CHANNELS[c].group === 'bones');
const SLOT_CHANNELS = ORDER.filter((c) => CURVE_CHANNELS[c].group === 'slots');

/** Channel table order, then target id: a track keeps its colour while the selection grows. */
function compareRefs(a: CurveRef, b: CurveRef): number {
  const byChannel = ORDER.indexOf(a.channel) - ORDER.indexOf(b.channel);
  if (byChannel !== 0) return byChannel;
  return a.target < b.target ? -1 : Number(a.target > b.target);
}

/**
 * Keyed numeric timelines of `animation` to draw: the channels of the selected keys, every curve channel of the
 * selected bones and slots, and the `pinned` refs (kept while the graph itself rewrites the key selection). Each
 * appears once; refs without keys and keys of other animations are left out. Empty without an animation.
 */
export function shownCurveRefs(doc: Doc, animation: Id | null, selection: SelectionSlice, pinned: readonly CurveRef[]): CurveRef[] {
  if (animation === null) return [];
  const wanted: CurveRef[] = [
    ...pinned,
    ...selection.keys.map(toCurveRef).filter((ref): ref is CurveRef => ref !== null),
    ...selection.bones.flatMap((target) => BONE_CHANNELS.map((channel) => ({ animation, channel, target }))),
    ...selection.slots.flatMap((target) => SLOT_CHANNELS.map((channel) => ({ animation, channel, target }))),
  ];
  const unique = new Map<string, CurveRef>();
  for (const ref of wanted) {
    if (ref.animation === animation && buildCurveTrack(doc, ref) !== null) unique.set(`${ref.channel}|${ref.target}`, ref);
  }
  return [...unique.values()].sort(compareRefs);
}
